// Enhanced Search V2 - Recherche complète avec tags et collections
// Utilise l'API de recherche Shopify pour trouver TOUS les produits

class EnhancedSearchV2 {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.abortController = null;

    // Mapping des termes de recherche vers les tags et collections
    this.searchMappings = this.buildSearchMappings();
  }

  /**
   * Construit la table de correspondance entre termes de recherche et tags/collections
   */
  buildSearchMappings() {
    const mappings = {};

    // Tags de collections MT (CO_XXX)
    const collections = [
      'BETA', 'DELTA', 'EPSILON', 'ERIS', 'GAMMA', 'GAMMAFEMME',
      'IOTA', 'MU', 'OMICRON', 'PHI', 'SIGMA', 'ZETA'
    ];

    collections.forEach(col => {
      mappings[col.toLowerCase()] = [`CO_${col}`];
    });

    // Mouvement
    mappings['automatique'] = ['AUTOMATIQUE', 'CA_AUTOMATIQUE'];
    mappings['auto'] = ['AUTOMATIQUE', 'CA_AUTOMATIQUE'];
    mappings['quartz'] = ['QUARTZ'];
    mappings['miyota'] = ['MIYOTA'];
    mappings['seagull'] = ['SEA_GULL'];
    mappings['seiko'] = ['SEIKO'];

    // Couleurs cadran
    mappings['noir'] = ['NOIR', 'BR_NOIR', 'ACIER_NOIR'];
    mappings['black'] = ['NOIR', 'BR_NOIR', 'ACIER_NOIR'];
    mappings['blanc'] = ['BLANC', 'BR_BLANC'];
    mappings['white'] = ['BLANC', 'BR_BLANC'];
    mappings['bleu'] = ['BLEU', 'BR_BLEU'];
    mappings['blue'] = ['BLEU', 'BR_BLEU'];
    mappings['vert'] = ['VERT', 'BR_VERT'];
    mappings['green'] = ['VERT', 'BR_VERT'];
    mappings['rouge'] = ['ROUGE', 'BR_ROUGE'];
    mappings['red'] = ['ROUGE', 'BR_ROUGE'];
    mappings['rose'] = ['ROSE', 'BR_ROSE'];
    mappings['pink'] = ['ROSE', 'BR_ROSE'];
    mappings['turquoise'] = ['TURQUOISE', 'BR_TURQUOISE'];
    mappings['kaki'] = ['KAKI', 'BR_KAKI'];
    mappings['marron'] = ['BR_MARRON'];
    mappings['brown'] = ['BR_MARRON'];
    mappings['orange'] = ['BR_ORANGE'];
    mappings['beige'] = ['BEIGE'];
    mappings['gris'] = ['GRIS'];
    mappings['grey'] = ['GRIS'];
    mappings['gray'] = ['GRIS'];
    mappings['champagne'] = ['CHAMPAGNE'];
    mappings['silver'] = ['SILVER'];
    mappings['argente'] = ['SILVER'];
    mappings['argent'] = ['SILVER'];
    mappings['violet'] = ['VIOLET'];
    mappings['purple'] = ['VIOLET'];

    // Matériaux
    mappings['acier'] = ['ACIER', 'ACIER_316L', 'ACIER_309L'];
    mappings['steel'] = ['ACIER', 'ACIER_316L', 'ACIER_309L'];
    mappings['dore'] = ['ACIER_DORE', 'ACIER_ET_DORE'];
    mappings['gold'] = ['ACIER_DORE', 'ACIER_ET_DORE'];
    mappings['cuir'] = ['CUIR'];
    mappings['leather'] = ['CUIR'];
    mappings['silicone'] = ['SILICONE'];
    mappings['caoutchouc'] = ['CAOUTCHOUC'];
    mappings['rubber'] = ['CAOUTCHOUC'];

    // Types de montre
    mappings['skeleton'] = ['SKELETON', 'CA_SKELETON', 'COEUR_OUVERT'];
    mappings['squelette'] = ['SKELETON', 'CA_SKELETON', 'COEUR_OUVERT'];
    mappings['coeur ouvert'] = ['COEUR_OUVERT'];
    mappings['coeur'] = ['COEUR_OUVERT'];
    mappings['open heart'] = ['COEUR_OUVERT'];
    mappings['chronographe'] = ['CA_CHRONOGRAPHE'];
    mappings['chronograph'] = ['CA_CHRONOGRAPHE'];
    mappings['chrono'] = ['CA_CHRONOGRAPHE'];
    mappings['plongeuse'] = ['PLONGEUSES'];
    mappings['plongee'] = ['PLONGEUSES'];
    mappings['dive'] = ['PLONGEUSES'];
    mappings['diver'] = ['PLONGEUSES'];
    mappings['arabic'] = ['CA_ARABIC'];
    mappings['dress'] = ['DRESS_WATCH'];
    mappings['carre'] = ['CARRE'];
    mappings['square'] = ['CARRE'];

    // Étanchéité
    mappings['3atm'] = ['3ATM'];
    mappings['5atm'] = ['5ATM'];
    mappings['10atm'] = ['10ATM'];

    // Caractéristiques
    mappings['saphir'] = ['SAPHIR', 'FB_SAPHIR'];
    mappings['sapphire'] = ['SAPHIR', 'FB_SAPHIR'];
    mappings['date'] = ['DATE', 'DAY_DATE'];
    mappings['moon phase'] = ['MOON_PHASE'];
    mappings['moonphase'] = ['MOON_PHASE'];
    mappings['meteorite'] = ['METEORITE'];
    mappings['mineral'] = ['MINERAL'];
    mappings['mocha'] = ['MOCHA'];

    // Genre
    mappings['homme'] = ['HOMME'];
    mappings['men'] = ['HOMME'];
    mappings['femme'] = ['FEMME', 'CO_GAMMAFEMME'];
    mappings['women'] = ['FEMME', 'CO_GAMMAFEMME'];
    mappings['woman'] = ['FEMME', 'CO_GAMMAFEMME'];
    mappings['unisexe'] = ['UNISEXE'];
    mappings['unisex'] = ['UNISEXE'];

    // Tailles
    mappings['34mm'] = ['34'];
    mappings['38mm'] = ['38'];
    mappings['40mm'] = ['40'];
    mappings['41mm'] = ['41'];
    mappings['42mm'] = ['42'];

    // Accessoires
    mappings['bracelet'] = ['BRACELET_14', 'BRACELET_20'];
    mappings['accessoire'] = ['ACCESSOIRES'];
    mappings['bijoux'] = ['BIJOUX'];

    return mappings;
  }

  /**
   * Retire les accents d'une chaîne pour permettre une comparaison insensible aux accents
   * (ex: "doré" et "dore" doivent être considérés comme équivalents)
   * @param {string} str
   * @returns {string}
   */
  stripAccents(str) {
    return str.normalize('NFD').replace(/\p{Diacritic}/gu, '');
  }

  /**
   * Expande une requête de recherche avec les tags correspondants
   * @param {string} query - Requête de recherche
   * @returns {string} - Requête expansée
   */
  expandQuery(query) {
    const queryLower = this.stripAccents(query.toLowerCase().trim());

    // Chercher tous les mappings correspondants
    const matchedTags = [];

    Object.keys(this.searchMappings).forEach(key => {
      const keyNormalized = this.stripAccents(key);
      if (queryLower.includes(keyNormalized) || keyNormalized.includes(queryLower)) {
        matchedTags.push(...this.searchMappings[key]);
      }
    });

    // Si on a trouvé des tags correspondants, créer une requête OR
    if (matchedTags.length > 0) {
      const uniqueTags = [...new Set(matchedTags)];
      const expandedQuery = `${query} OR ${uniqueTags.join(' OR ')}`;
      return expandedQuery;
    }

    return query;
  }

  /**
   * Recherche complète (produits + pages + collections + articles)
   * @param {string} query - Terme de recherche
   * @returns {Promise<Object>} - Objet avec products, pages, collections, articles
   */
  async searchAll(query) {
    // Vérifier le cache
    const cacheKey = query.toLowerCase().trim();
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.results;
    }

    // Annuler la requête précédente si elle existe
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const [products, pages, collections, articles] = await Promise.all([
        this.searchProducts(query),
        this.searchPages(query),
        this.searchCollections(query),
        this.searchArticles(query)
      ]);

      const results = {
        products: products,
        pages: pages,
        collections: collections,
        articles: articles
      };

      // Mettre en cache
      this.cache.set(cacheKey, {
        results: results,
        timestamp: Date.now()
      });

      return results;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Requête annulée');
        return { products: [], pages: [], collections: [], articles: [] };
      }

      console.error('Erreur recherche:', error);
      return { products: [], pages: [], collections: [], articles: [] };
    }
  }

  /**
   * Recherche complète de produits (titre + tags + collections)
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des produits trouvés
   */
  async searchProducts(query) {
    try {
      // Expander la requête avec les tags correspondants
      const expandedQuery = this.expandQuery(query);

      // Utiliser l'API Predictive Search avec la limite maximale
      const products = await this.searchProductsPredictiveMultiple(expandedQuery);

      // Trier par pertinence
      const sortedProducts = this.sortByRelevance(products, query);

      return sortedProducts;
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Requête annulée');
        return [];
      }

      console.error('Erreur recherche produits:', error);
      return [];
    }
  }

  /**
   * Recherche de pages
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des pages trouvées
   */
  async searchPages(query) {
    try {
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=page&resources[limit]=10`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const pages = data.resources?.results?.pages || [];

      return pages.map(page => ({
        id: page.id,
        title: page.title,
        url: page.url,
        type: 'page'
      }));
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error searching pages:', error);
      return [];
    }
  }

  /**
   * Recherche de collections
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des collections trouvées
   */
  async searchCollections(query) {
    try {
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=collection&resources[limit]=10`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const collections = data.resources?.results?.collections || [];

      return collections.map(collection => ({
        id: collection.id,
        title: collection.title,
        url: collection.url,
        image: this.resizeShopifyImage(collection.image || collection.featured_image?.url || '', 400),
        type: 'collection'
      }));
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error searching collections:', error);
      return [];
    }
  }

  /**
   * Recherche d'articles de blog
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des articles trouvés
   */
  async searchArticles(query) {
    try {
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=article&resources[limit]=10`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const articles = data.resources?.results?.articles || [];

      return articles.map(article => ({
        id: article.id,
        title: article.title,
        url: article.url,
        type: 'article'
      }));
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error searching articles:', error);
      return [];
    }
  }

  /**
   * Recherche en parsant le HTML de la page de recherche
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des produits
   */
  async searchProductsHTML(query) {
    try {
      // Expander la requête avec les tags correspondants
      const expandedQuery = this.expandQuery(query);

      // Récupérer tous les produits de toutes les pages
      const allProducts = await this.fetchAllPages(expandedQuery);

      // Trier par pertinence
      return this.sortByRelevance(allProducts, query);
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error:', error);
      return [];
    }
  }

  /**
   * Récupère tous les produits de toutes les pages de résultats
   * @param {string} expandedQuery - Requête expansée
   * @returns {Promise<Array>} - Tous les produits
   */
  async fetchAllPages(expandedQuery) {
    const allProducts = [];
    const seenHandles = new Set();
    let page = 1;
    let hasMorePages = true;

    while (hasMorePages && page <= 5) { // Limite à 5 pages (120 produits max)
      const searchUrl = `${window.Shopify.routes.root}search?q=${encodeURIComponent(expandedQuery)}&type=product&page=${page}`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        if (page === 1) {
          // Si la première page échoue, fallback à Predictive Search
          return await this.searchProductsPredictive(expandedQuery);
        }
        break;
      }

      const html = await response.text();

      // Vérifier si on a reçu du HTML valide
      if (html.length < 100) {
        break;
      }

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extraire les produits du HTML - Le thème Impact utilise <product-card> (custom element)
      const productElements = doc.querySelectorAll('product-card');

      // Si aucun produit trouvé, on a atteint la fin
      if (productElements.length === 0) {
        hasMorePages = false;
        break;
      }

      // Tableau pour les produits de cette page
      const pageProducts = [];

      productElements.forEach((el, index) => {
        // Trouver le lien produit
        const link = el.querySelector('a[href*="/products/"]') || (el.tagName.toLowerCase() === 'a' && el.href?.includes('/products/') ? el : null);

        if (!link) return;

        const url = link.getAttribute('href') || link.href;
        if (!url || !url.includes('/products/')) return;

        const handle = url.split('/products/')[1]?.split('?')[0]?.split('#')[0];
        if (!handle) return;

        if (seenHandles.has(handle)) return;

        seenHandles.add(handle);

        // Titre du produit - Impact theme utilise .product-card__title
        const titleEl = el.querySelector('.product-card__title');
        let title = '';

        if (titleEl) {
          // Le titre peut être dans un <a> ou directement dans le <h3>/<span>
          const titleLink = titleEl.querySelector('a');
          title = titleLink ? titleLink.textContent.trim() : titleEl.textContent.trim();
        }

        if (!title) return;

        // Image du produit
        const imageEl = el.querySelector('img, [loading="lazy"]');
        let imageSrc = '';
        if (imageEl) {
          imageSrc = imageEl.getAttribute('src') || imageEl.getAttribute('data-src') || imageEl.src || '';
          // Si l'image est lazy-loaded, elle peut être dans srcset
          if (!imageSrc && imageEl.srcset) {
            const srcset = imageEl.srcset.split(',')[0].trim().split(' ')[0];
            imageSrc = srcset;
          }
        }

        // Prix du produit - Rechercher dans price-list qui contient sale-price
        const priceListEl = el.querySelector('.price-list');
        let price = '';
        let comparePrice = '';

        if (priceListEl) {
          // Chercher le prix de vente (actuel) - exclure le texte .sr-only
          const salePriceEl = priceListEl.querySelector('sale-price');
          if (salePriceEl) {
            // Cloner l'élément pour ne pas modifier l'original
            const clone = salePriceEl.cloneNode(true);
            // Supprimer les éléments .sr-only (qui contiennent "Prix de vente")
            const srOnly = clone.querySelector('.sr-only');
            if (srOnly) srOnly.remove();
            price = clone.textContent.trim();
          }

          // Chercher le prix comparé (barré) - exclure le texte .sr-only
          const comparePriceEl = priceListEl.querySelector('compare-at-price');
          if (comparePriceEl && !comparePriceEl.hasAttribute('hidden')) {
            // Cloner l'élément pour ne pas modifier l'original
            const clone = comparePriceEl.cloneNode(true);
            // Supprimer les éléments .sr-only
            const srOnly = clone.querySelector('.sr-only');
            if (srOnly) srOnly.remove();
            comparePrice = clone.textContent.trim();
          }
        }

        // Fallback si price-list n'existe pas
        if (!price) {
          const priceEl = el.querySelector('.price, [class*="price"], .money');
          price = priceEl ? priceEl.textContent.trim() : '';
        }

        // Disponibilité (check pour badge sold-out)
        const soldOutBadge = el.querySelector('.badge--sold-out, [class*="sold-out"], [class*="out-of-stock"]');
        const available = !soldOutBadge;

        // Extraire les badges avec leur type
        const badges = [];
        const badgeElements = el.querySelectorAll('.badge:not(.badge--sold-out)');
        badgeElements.forEach(badgeEl => {
          const badgeText = badgeEl.textContent.trim();
          if (badgeText) {
            // Déterminer le type de badge en fonction du contenu
            const isDiscount = badgeText.toLowerCase().includes('économisez') || badgeText.includes('%');
            badges.push({
              text: badgeText,
              type: isDiscount ? 'discount' : 'custom'
            });
          }
        });

        pageProducts.push({
          id: handle,
          title: title,
          url: url.startsWith('http') ? url : `${window.location.origin}${url}`,
          handle: handle,
          image: this.resizeShopifyImage(imageSrc, 800),
          available: available,
          price: price,
          compare_at_price: comparePrice,
          badges: badges,
          relevance: 0 // Sera calculé plus tard
        });
      });

      // Ajouter les produits de cette page au tableau global
      allProducts.push(...pageProducts);

      // Si on a moins de 24 produits, c'est la dernière page
      if (productElements.length < 24) {
        hasMorePages = false;
      } else {
        page++;
      }
    }

    return allProducts;
  }


  /**
   * Recherche avec l'API Predictive Search - Méthode améliorée qui récupère TOUS les produits
   * L'API suggest.json retourne max 10 résultats, donc on utilise l'API collection pour avoir tous les produits
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des produits
   */
  async searchProductsPredictiveMultiple(query) {
    try {
      // Utiliser l'API /search.json qui peut retourner plus de résultats
      const searchUrl = `${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}&type=product&view=json`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        // Fallback sur suggest.json si l'API search.json ne fonctionne pas
        return await this.searchProductsPredictive(query);
      }

      const html = await response.text();

      // Parser le HTML pour extraire les produits
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const productElements = doc.querySelectorAll('product-card');

      const products = [];
      const seenHandles = new Set();

      productElements.forEach(element => {
        const link = element.querySelector('a[href*="/products/"]');
        if (!link) return;

        const url = link.getAttribute('href');
        const handle = url.split('/products/')[1]?.split('?')[0];

        if (!handle || seenHandles.has(handle)) return;
        seenHandles.add(handle);

        const titleElement = element.querySelector('.product-card__title a, a.bold');
        const imageElement = element.querySelector('img');

        // Prix du produit - Rechercher dans price-list
        const priceListEl = element.querySelector('.price-list');
        let price = '';
        let comparePrice = '';

        if (priceListEl) {
          // Chercher le prix de vente (actuel) - exclure le texte .sr-only
          const salePriceEl = priceListEl.querySelector('sale-price');
          if (salePriceEl) {
            // Cloner l'élément pour ne pas modifier l'original
            const clone = salePriceEl.cloneNode(true);
            // Supprimer les éléments .sr-only (qui contiennent "Prix de vente")
            const srOnly = clone.querySelector('.sr-only');
            if (srOnly) srOnly.remove();
            price = clone.textContent.trim();
          }

          // Chercher le prix comparé (barré) - exclure le texte .sr-only
          const comparePriceEl = priceListEl.querySelector('compare-at-price');
          if (comparePriceEl && !comparePriceEl.hasAttribute('hidden')) {
            // Cloner l'élément pour ne pas modifier l'original
            const clone = comparePriceEl.cloneNode(true);
            // Supprimer les éléments .sr-only
            const srOnly = clone.querySelector('.sr-only');
            if (srOnly) srOnly.remove();
            comparePrice = clone.textContent.trim();
          }
        }

        // Fallback si price-list n'existe pas
        if (!price) {
          const priceElement = element.querySelector('sale-price span:last-child, .price');
          price = priceElement?.textContent?.trim() || '';
        }

        const title = titleElement?.textContent?.trim() || '';
        const image = imageElement?.getAttribute('src') || '';

        // Extraire les badges avec leur type
        const badges = [];
        const badgeElements = element.querySelectorAll('.badge:not(.badge--sold-out)');
        badgeElements.forEach(badgeEl => {
          const badgeText = badgeEl.textContent.trim();
          if (badgeText) {
            // Déterminer le type de badge en fonction du contenu
            const isDiscount = badgeText.toLowerCase().includes('économisez') || badgeText.includes('%');
            badges.push({
              text: badgeText,
              type: isDiscount ? 'discount' : 'custom'
            });
          }
        });

        products.push({
          id: handle,
          title: title,
          url: url.startsWith('http') ? url : window.Shopify.routes.root.replace(/\/$/, '') + url,
          handle: handle,
          image: this.resizeShopifyImage(image, 800),
          available: true,
          price: price,
          compare_at_price: comparePrice,
          badges: badges,
          relevance: this.calculateRelevance(title, query),
          // Markup réel du <product-card> tel que rendu par Shopify (mêmes classes/CSS que la page collection)
          cardHtml: element.outerHTML
        });
      });

      // Si on n'a trouvé aucun produit, fallback sur suggest.json
      if (products.length === 0) {
        return await this.searchProductsPredictive(query);
      }

      return products;
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error:', error);
      // Fallback sur suggest.json en cas d'erreur
      return await this.searchProductsPredictive(query);
    }
  }

  /**
   * Recherche avec l'API Predictive Search (fallback - limitée à 10 résultats)
   * @param {string} query - Terme de recherche
   * @returns {Promise<Array>} - Liste des produits
   */
  async searchProductsPredictive(query) {
    try {
      const searchUrl = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10`;

      const response = await fetch(searchUrl, {
        signal: this.abortController?.signal
      });

      if (!response.ok) {
        return [];
      }

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      return products.map(product => ({
        id: product.id,
        title: product.title,
        url: product.url,
        handle: product.handle,
        image: this.resizeShopifyImage(product.image || product.featured_image?.url || '', 800),
        available: product.available !== false,
        price: product.price_formatted || product.price || '',
        compare_at_price: product.compare_at_price_formatted || product.compare_at_price || '',
        badges: [], // L'API suggest.json ne retourne pas les badges, ils seront vides
        relevance: this.calculateRelevance(product.title, query)
      }));
    } catch (error) {
      if (error.name === 'AbortError') {
        return [];
      }
      console.error('[EnhancedSearch] Error:', error);
      return [];
    }
  }

  /**
   * Calcule la pertinence d'un produit par rapport à la requête
   * @param {string} title - Titre du produit
   * @param {string} query - Requête de recherche
   * @param {Array} tags - Tags du produit (optionnel)
   * @returns {number} - Score de pertinence (plus élevé = plus pertinent)
   */
  calculateRelevance(title, query, tags = []) {
    const queryLower = query.toLowerCase().trim();
    const titleLower = title.toLowerCase();
    const tagsLower = tags.map(t => t.toLowerCase());

    let score = 0;

    // 1. Match exact dans le titre = priorité maximale
    if (titleLower === queryLower) {
      score += 1000;
    }
    // 2. Titre commence par la requête
    else if (titleLower.startsWith(queryLower)) {
      score += 500;
    }
    // 3. Titre contient la requête (mot entier)
    else if (new RegExp(`\\b${this.escapeRegex(queryLower)}\\b`, 'i').test(titleLower)) {
      score += 300;
    }
    // 4. Titre contient la requête (n'importe où)
    else if (titleLower.includes(queryLower)) {
      score += 100;
    }

    // 5. Match dans les tags
    tagsLower.forEach(tag => {
      if (tag === queryLower) {
        score += 200; // Match exact dans un tag
      } else if (tag.includes(queryLower) || queryLower.includes(tag)) {
        score += 50; // Match partiel dans un tag
      }
    });

    // 6. Bonus pour correspondance CO_BETA, CO_DELTA, etc.
    const collectionPattern = /^co[_\s-]?(\w+)$/i;
    const matchCollection = query.match(collectionPattern);
    if (matchCollection) {
      const collectionName = matchCollection[1];
      tagsLower.forEach(tag => {
        if (tag.includes(collectionName.toLowerCase())) {
          score += 150;
        }
      });
    }

    return score;
  }

  /**
   * Trie les produits par pertinence
   */
  sortByRelevance(products, query) {
    // Calculer le score de pertinence pour chaque produit
    products.forEach(product => {
      product.relevance = this.calculateRelevance(product.title, query);
    });

    return products.sort((a, b) => {
      // D'abord par score de pertinence
      const scoreDiff = b.relevance - a.relevance;
      if (scoreDiff !== 0) return scoreDiff;

      // Ensuite par disponibilité
      if (a.available !== b.available) {
        return a.available ? -1 : 1;
      }

      // Enfin par ordre alphabétique
      return a.title.localeCompare(b.title);
    });
  }

  /**
   * Échappe les caractères spéciaux pour regex
   */
  escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Redimensionne une URL d'image Shopify pour une meilleure qualité
   * @param {string} imageUrl - URL de l'image originale
   * @param {number} width - Largeur souhaitée en pixels
   * @returns {string} - URL de l'image redimensionnée
   */
  resizeShopifyImage(imageUrl, width = 800) {
    if (!imageUrl) return '';

    // Si l'URL contient déjà un paramètre de taille, le remplacer
    let newUrl = imageUrl.replace(/(_\d+x\d+|\d+x\d+|_\d+x|x\d+)(\.[^.]+)$/, `_${width}x$2`);

    // Si pas de pattern de redimensionnement trouvé, ajouter le paramètre width
    if (newUrl === imageUrl) {
      const separator = imageUrl.includes('?') ? '&' : '?';
      newUrl = `${imageUrl}${separator}width=${width}`;
    }

    return newUrl;
  }

  /**
   * Compte le nombre total de résultats pour une requête
   */
  async getResultsCount(query) {
    try {
      const products = await this.searchProducts(query);
      return products.length;
    } catch (error) {
      console.error('Erreur comptage résultats:', error);
      return 0;
    }
  }
}

// Export global
window.EnhancedSearchV2 = EnhancedSearchV2;
